import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
  withCredentials: true
});

// Request interceptor to add auth token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Attach active workspace id so backend enforces workspace isolation
  // for endpoints whose path does not include workspace_id.
  const activeWs = localStorage.getItem('active_workspace_id');
  if (activeWs) {
    config.headers['X-Workspace-Id'] = activeWs;
  }
  return config;
});

// Response interceptor for token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      const refreshToken = localStorage.getItem('refresh_token');
      if (refreshToken) {
        try {
          const response = await axios.post(`${API_URL}/api/auth/refresh`, {
            refresh_token: refreshToken
          });
          
          const { access_token } = response.data;
          localStorage.setItem('access_token', access_token);
          originalRequest.headers.Authorization = `Bearer ${access_token}`;
          
          return api(originalRequest);
        } catch (refreshError) {
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          window.location.href = '/login';
        }
      }
    }
    
    return Promise.reject(error);
  }
);

// Auth APIs
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  signup: (email, password, full_name) => api.post('/auth/signup', { email, password, full_name }),
  logout: () => api.post('/auth/logout'),
  me: () => api.get('/auth/me'),
  processSession: (sessionId) => api.post('/auth/session', { session_id: sessionId }),
  refresh: (refreshToken) => api.post('/auth/refresh', { refresh_token: refreshToken }),
  changePassword: (current_password, new_password) => api.post('/auth/change-password', { current_password, new_password }),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  validateResetToken: (token) => api.get('/auth/validate-reset-token', { params: { token } }),
  resetPassword: (token, new_password) => api.post('/auth/reset-password', { token, new_password }),
  listSessions: () => api.get('/auth/sessions'),
  revokeSession: (sessionId) => api.delete(`/auth/sessions/${sessionId}`),
  setOnboardingPersona: (persona) => api.post('/auth/onboarding-persona', { persona })
};

// Workspace APIs
export const notificationsAPI = {
  list: (params = {}) => api.get('/notifications', { params }),
  markRead: (id) => api.post(`/notifications/${id}/read`),
  markAllRead: () => api.post('/notifications/read-all'),
  getPrefs: () => api.get('/notifications/preferences'),
  savePrefs: (prefs) => api.put('/notifications/preferences', prefs),
};

export const workspaceAPI = {
  list: () => api.get('/workspaces'),
  rename: (workspaceId, name) => api.put(`/workspaces/${workspaceId}`, { name }),
  get: (workspaceId) => api.get(`/workspaces/${workspaceId}`),
  create: (name) => api.post('/workspaces', { name }),
  members: (workspaceId) => api.get(`/workspaces/${workspaceId}/members`),
  activity: (workspaceId, params = {}) => api.get(`/workspaces/${workspaceId}/activity`, { params }),
};

// Analysis APIs
export const analysisAPI = {
  list: (workspaceId, params = {}) => 
    api.get(`/workspaces/${workspaceId}/analyses`, { params }),
  get: (workspaceId, analysisId) => 
    api.get(`/workspaces/${workspaceId}/analyses/${analysisId}`),
  create: (workspaceId, companyInput) => 
    api.post(`/workspaces/${workspaceId}/analyses`, { company_input: companyInput }),
  process: (workspaceId, analysisId) => 
    api.post(`/workspaces/${workspaceId}/analyses/${analysisId}/process`),
  reanalyze: (workspaceId, analysisId) =>
    api.post(`/workspaces/${workspaceId}/analyses/${analysisId}/reanalyze`),
  scenarioRerun: (workspaceId, analysisId, scenarioText) =>
    api.post(`/workspaces/${workspaceId}/analyses/${analysisId}/scenario-rerun`, { scenario_text: scenarioText }),
  bulkReanalyze: (workspaceId) =>
    api.post(`/workspaces/${workspaceId}/analyses/bulk-reanalyze`),
  staleSummary: (workspaceId) =>
    api.get(`/workspaces/${workspaceId}/analyses/stale-summary`),
  bulkReanalyzeStale: (workspaceId) =>
    api.post(`/workspaces/${workspaceId}/analyses/bulk-reanalyze-stale`),
  poll: (workspaceId, analysisId) =>
    api.get(`/workspaces/${workspaceId}/analyses/${analysisId}/poll`),
  passDecision: (workspaceId, analysisId, data) =>
    api.post(`/workspaces/${workspaceId}/analyses/${analysisId}/pass`, data),
  getDecisions: (workspaceId, analysisId) =>
    api.get(`/workspaces/${workspaceId}/analyses/${analysisId}/decisions`),
  refresh: (workspaceId, analysisId) =>
    api.post(`/workspaces/${workspaceId}/analyses/${analysisId}/refresh`),
  bulkRefresh: (workspaceId) =>
    api.post(`/workspaces/${workspaceId}/analyses/bulk-refresh`),
  presetScores: (workspaceId, analysisId) =>
    api.get(`/workspaces/${workspaceId}/analyses/${analysisId}/preset-scores`),
  deepScorePreset: (workspaceId, analysisId, presetId) =>
    api.post(`/workspaces/${workspaceId}/analyses/${analysisId}/preset-scores/${presetId}/deep`),
};

// Pricing API
export const pricingAPI = {
  get: () => api.get('/pricing')
};

// Billing API (Part 10/11/12)
export const billingAPI = {
  usage: () => api.get('/billing/usage'),
  tier: () => api.get('/billing/tier'),
  tiers: () => api.get('/billing/tiers'),
  batchAddons: () => api.get('/billing/batch-addons'),
  purchaseOverage: (quantity, originUrl) =>
    api.post('/billing/purchase-overage', { quantity, origin_url: originUrl }),
  subscribe: (tierId, billingPeriod, originUrl) =>
    api.post('/billing/subscribe', { tier_id: tierId, billing_period: billingPeriod, origin_url: originUrl }),
  checkoutStatus: (sessionId) =>
    api.get(`/billing/checkout-status/${sessionId}`),
  subscribeWorkspace: (workspaceName, originUrl) =>
    api.post('/billing/subscribe-workspace', { workspace_name: workspaceName, origin_url: originUrl }),
  workspaceInfo: () =>
    api.get('/billing/workspace-info'),
  cancelSubscription: () =>
    api.post('/billing/cancel-subscription'),
};

// Founder Tools (only accessible to emails in FOUNDER_EMAILS env)
export const founderAPI = {
  me: () => api.get('/founder/me'),
  selfPromote: () => api.post('/founder/self-promote'),
};

// API Keys (Part 15)
export const apiKeysAPI = {
  list: () => api.get('/api-keys'),
  create: (name, scopes) => api.post('/api-keys', { name, scopes }),
  update: (keyId, data) => api.put(`/api-keys/${keyId}`, data),
  revoke: (keyId) => api.delete(`/api-keys/${keyId}`),
};

// Team APIs
export const teamAPI = {
  list: () => api.get('/teams'),
  create: (name) => api.post('/teams', { name }),
  get: (teamId) => api.get(`/teams/${teamId}`),
  invite: (teamId, email) => api.post(`/teams/${teamId}/invite`, { email }),
  removeMember: (teamId, email) => api.delete(`/teams/${teamId}/members/${email}`),
  activity: (teamId, limit = 50) => api.get(`/teams/${teamId}/activity`, { params: { limit } }),
};

// Notes APIs
export const notesAPI = {
  list: () => api.get('/notes'),
  create: (data) => api.post('/notes', data),
  update: (noteId, data) => api.put(`/notes/${noteId}`, data),
  delete: (noteId) => api.delete(`/notes/${noteId}`),
};

// Folders APIs
export const foldersAPI = {
  list: () => api.get('/folders'),
  create: (data) => api.post('/folders', data),
  update: (folderId, data) => api.put(`/folders/${folderId}`, data),
  delete: (folderId) => api.delete(`/folders/${folderId}`),
  addMember: (folderId, email) => api.post(`/folders/${folderId}/members`, { email }),
};

// Batch Analysis APIs
export const batchAPI = {
  create: (workspaceId, companies) => api.post(`/workspaces/${workspaceId}/batch`, { companies }),
  list: (workspaceId) => api.get(`/workspaces/${workspaceId}/batch`),
  get: (workspaceId, batchId) => api.get(`/workspaces/${workspaceId}/batch/${batchId}`),
};

// Export APIs
export const exportAPI = {
  list: (params = {}) => api.get('/exports', { params }),
  create: (data) => api.post('/exports', data),
  get: (exportId) => api.get(`/exports/${exportId}`),
  delete: (exportId) => api.delete(`/exports/${exportId}`),
};

// Monitor APIs
export const monitorAPI = {
  list: () => api.get('/monitor'),
  create: (data) => api.post('/monitor', data),
  get: (monitorId) => api.get(`/monitor/${monitorId}`),
  update: (monitorId, data) => api.put(`/monitor/${monitorId}`, data),
  delete: (monitorId) => api.delete(`/monitor/${monitorId}`),
  getCadence: () => api.get('/monitor/cadence'),
  setCadence: (cadence) => api.put('/monitor/cadence', { cadence }),
  checkNow: (monitorId) => api.post(`/monitor/${monitorId}/check`),
};

// Help assistant API
export const helpAPI = {
  ask: (question, history = []) => api.post('/help/ask', { question, history }),
};

// Search API
export const searchAPI = {
  search: (query) => api.get('/search', { params: { q: query } }),
};

// Contacts aggregator API — flattens key_contacts/contacts from every
// analysis in the workspace into a single list.
export const contactsAPI = {
  list: (params = {}) => api.get('/contacts', { params }),
};

// Saved Articles APIs
export const articlesAPI = {
  list: (folder) => api.get('/articles', { params: folder ? { folder } : {} }),
  folders: () => api.get('/articles/folders'),
  save: (data) => api.post('/articles', data),
  delete: (articleId) => api.delete(`/articles/${articleId}`),
  summaries: () => api.get('/articles/summaries'),
  summarize: (data) => api.post('/articles/summarize', data),
};

// Share-link tokens — public read-only URLs for a single analysis.
export const shareAPI = {
  create: (analysisId) => api.post(`/analyses/${analysisId}/share-token`),
  revoke: (analysisId) => api.delete(`/analyses/${analysisId}/share-token`),
};

export const icpAPI = {
  get: () => api.get('/icp'),
  save: (profile) => api.post('/icp', profile),
  update: (profile) => api.put('/icp', profile),
  suggestions: () => api.get('/icp/suggestions'),
  presets: () => api.get('/icp/presets'),
  createPreset: (name, copyActive = true, scope = 'personal') =>
    api.post('/icp/presets', { name, copy_active: copyActive, scope }),
  renamePreset: (presetId, name) => api.put(`/icp/presets/${presetId}`, { name }),
  sharePreset: (presetId) => api.post(`/icp/presets/${presetId}/share`),
  deletePreset: (presetId) => api.delete(`/icp/presets/${presetId}`),
  activatePreset: (presetId) => api.post(`/icp/presets/${presetId}/activate`),
};

export default api;
